import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

type SaveState = "idle" | "saving" | "saved" | "error";

interface HighlightsEditorProps {
  label?: string;
  items: string[];
  onChange: (items: string[]) => void;
  onSave: (items: string[]) => Promise<void> | void;
  maxItems?: number;
  maxLen?: number;
  placeholder?: string;
  addLabel?: string;
  helperText?: string;
}

const HighlightsEditor = ({
  label = "Junior golf highlights",
  items,
  onChange,
  onSave,
  maxItems = 10,
  maxLen = 120,
  placeholder = "First place 2025 AJGA Preview at Bethpage 214 (74, 69, 71)",
  addLabel = "Add",
  helperText,
}: HighlightsEditorProps) => {
  const [draft, setDraft] = useState<string>("");
  const [status, setStatus] = useState<SaveState>("idle");
  const [error, setError] = useState<string | null>(null);

  const trySave = async (next: string[]) => {
    try {
      setStatus("saving");
      setError(null);
      await onSave(next);
      setStatus("saved");
      window.setTimeout(() => setStatus("idle"), 1500);
    } catch (e: any) {
      setStatus("error");
      setError(String(e?.message || "Save failed"));
    }
  };

  const add = async () => {
    const v = draft.trim();
    if (!v) return;
    if (items.length >= maxItems) {
      setError(`You can add up to ${maxItems} highlights`);
      setStatus("error");
      return;
    }
    const clipped = v.slice(0, maxLen);
    const next = [clipped, ...items];
    onChange(next);
    setDraft("");
    await trySave(next);
  };

  const remove = async (idx: number) => {
    const next = items.filter((_, i) => i !== idx);
    onChange(next);
    await trySave(next);
  };

  return (
    <div>
      <div className="text-xs text-muted-foreground/60 mb-1">{label}</div>
      <div className="flex items-center gap-2 mb-2">
        <Input
          placeholder={placeholder}
          className="w-full placeholder:text-muted-foreground/60 placeholder:italic"
          value={draft}
          onChange={e => {
            if (error) setError(null);
            setDraft(e.target.value);
          }}
          onKeyDown={async e => {
            if (e.key === "Enter") {
              e.preventDefault();
              await add();
            }
          }}
        />
        <Button type="button" onClick={add}>
          {addLabel}
        </Button>
      </div>
      {helperText ? <div className="text-xs text-muted-foreground mb-2">{helperText}</div> : null}
      <div className="space-y-2">
        {items.map((h, idx) => (
          <div
            key={`${h}-${idx}`}
            className="flex items-center justify-between border rounded-md px-3 py-2"
          >
            <div className="text-sm">{h}</div>
            <Button variant="ghost" size="sm" onClick={() => remove(idx)}>
              ×
            </Button>
          </div>
        ))}
      </div>
      <div className={`text-xs mt-1 ${error ? "text-destructive" : "text-muted-foreground"}`}>
        {error ? error : status === "saving" ? "Saving…" : status === "saved" ? "Saved" : "\u00A0"}
      </div>
    </div>
  );
};

export default HighlightsEditor;
